import ApiService from './api';
import { safeArray } from '../utils/money';
import {
  VirtualCard,
  Cardholder,
  CardStatus,
  SpendingControls,
  SpendingLimit
} from '@/types';

export interface CreateCardholderRequest {
  name: string;
  email: string;
  phone?: string;
  billing_address: {
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  metadata?: Record<string, any>;
}

export interface CreateCardRequest {
  cardholder_id: string;
  type: 'virtual' | 'physical';
  currency: string;
  spending_controls?: SpendingControls;
  metadata?: Record<string, any>;
}

export interface UpdateSpendingControlsRequest {
  spending_limits: SpendingLimit[];
  allowed_categories?: string[];
  blocked_categories?: string[];
}

export interface CardTransaction {
  id: string;
  card_id: string;
  amount: number;
  currency: string;
  merchant_name: string;
  merchant_category: string;
  authorization_code?: string;
  status: 'pending' | 'approved' | 'declined';
  created_at: string;
  settled_at?: string;
  dispute_id?: string;
  metadata?: Record<string, any>;
}

export interface CardFilters {
  status?: CardStatus[];
  cardholder_id?: string;
  type?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export interface TransactionFilters {
  card_id?: string;
  status?: string[];
  date_from?: string;
  date_to?: string;
  amount_min?: number;
  amount_max?: number;
  merchant_category?: string;
  search?: string;
}

export class CardService {
  // 持卡人管理
  static async createCardholder(data: CreateCardholderRequest): Promise<Cardholder> {
    const response = await ApiService.post<Cardholder>('/merchant/issuing/cardholders', data);
    return response.data;
  }

  static async listCardholders(
    page: number = 1,
    limit: number = 20,
    search?: string
  ): Promise<{
    data: Cardholder[];
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  }> {
    const params: any = { page, limit };
    if (search) params.search = search;

    const response = await ApiService.get('/merchant/issuing/cardholders', params);
    return {
      ...response.data,
      data: safeArray<Cardholder>(response.data?.data),
    };
  }

  static async getCardholder(id: string): Promise<Cardholder> {
    const response = await ApiService.get<Cardholder>(`/merchant/issuing/cardholders/${id}`);
    return response.data;
  }

  static async updateCardholder(
    id: string,
    data: Partial<CreateCardholderRequest>
  ): Promise<Cardholder> {
    const response = await ApiService.put<Cardholder>(`/merchant/issuing/cardholders/${id}`, data);
    return response.data;
  }

  // 卡片管理
  static async createCard(data: CreateCardRequest): Promise<VirtualCard> {
    const response = await ApiService.post<VirtualCard>('/merchant/issuing/cards', data);
    return response.data;
  }

  static async listCards(
    page: number = 1,
    limit: number = 20,
    filters?: CardFilters
  ): Promise<{
    data: VirtualCard[];
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  }> {
    const params = {
      page,
      limit,
      ...filters,
    };
    const response = await ApiService.get('/merchant/issuing/cards', params);
    return {
      ...response.data,
      data: safeArray<VirtualCard>(response.data?.data),
    };
  }

  static async getCard(id: string): Promise<VirtualCard> {
    const response = await ApiService.get<VirtualCard>(`/merchant/issuing/cards/${id}`);
    return response.data;
  }

  static async updateCard(id: string, data: Partial<VirtualCard>): Promise<VirtualCard> {
    const response = await ApiService.put<VirtualCard>(`/merchant/issuing/cards/${id}`, data);
    return response.data;
  }

  // 卡片状态管理
  static async activateCard(id: string): Promise<VirtualCard> {
    const response = await ApiService.post<VirtualCard>(`/merchant/issuing/cards/${id}/activate`);
    return response.data;
  }

  static async blockCard(id: string, reason?: string): Promise<VirtualCard> {
    const response = await ApiService.post<VirtualCard>(`/merchant/issuing/cards/${id}/block`, {
      reason,
    });
    return response.data;
  }

  static async unblockCard(id: string): Promise<VirtualCard> {
    const response = await ApiService.post<VirtualCard>(`/merchant/issuing/cards/${id}/unblock`);
    return response.data;
  }

  static async cancelCard(id: string, reason?: string): Promise<void> {
    await ApiService.delete(`/merchant/issuing/cards/${id}`, {
      data: { reason },
    });
  }

  // 卡片详情和安全信息
  static async getCardDetails(id: string): Promise<{
    card_number: string;
    expiry_month: number;
    expiry_year: number;
    cvv: string;
    cardholder_name: string;
  }> {
    const response = await ApiService.get(`/merchant/issuing/cards/${id}/details`);
    return response.data;
  }

  static async getCardPin(id: string): Promise<{
    pin: string;
    encrypted_pin: string;
  }> {
    const response = await ApiService.get(`/merchant/issuing/cards/${id}/pin`);
    return response.data;
  }

  static async updateCardPin(id: string, newPin: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await ApiService.put(`/merchant/issuing/cards/${id}/pin`, {
      new_pin: newPin,
    });
    return response.data;
  }

  // 支出控制
  static async updateSpendingControls(
    cardId: string,
    controls: UpdateSpendingControlsRequest
  ): Promise<VirtualCard> {
    const response = await ApiService.put<VirtualCard>(
      `/merchant/issuing/cards/${cardId}/spending-controls`,
      controls
    );
    return response.data;
  }

  static async getSpendingControls(cardId: string): Promise<SpendingControls> {
    const response = await ApiService.get<SpendingControls>(
      `/merchant/issuing/cards/${cardId}/spending-controls`
    );
    return response.data;
  }

  // 交易管理
  static async listTransactions(
    page: number = 1,
    limit: number = 20,
    filters?: TransactionFilters
  ): Promise<{
    data: CardTransaction[];
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  }> {
    const params = {
      page,
      limit,
      ...filters,
    };
    const response = await ApiService.get('/merchant/issuing/transactions', params);
    return {
      ...response.data,
      data: safeArray<CardTransaction>(response.data?.data),
    };
  }

  static async getTransaction(id: string): Promise<CardTransaction> {
    const response = await ApiService.get<CardTransaction>(`/merchant/issuing/transactions/${id}`);
    return response.data;
  }

  static async disputeTransaction(id: string, reason: string): Promise<{
    dispute_id: string;
    status: string;
    message: string;
  }> {
    const response = await ApiService.post(`/merchant/issuing/transactions/${id}/dispute`, {
      reason,
    });
    return response.data;
  }

  // 控制规则
  static async createControl(data: {
    name: string;
    type: 'spending_limit' | 'merchant_category' | 'geographic' | 'velocity';
    parameters: any;
    enabled: boolean;
  }): Promise<{
    id: string;
    name: string;
    type: string;
    parameters: any;
    enabled: boolean;
    created_at: string;
  }> {
    const response = await ApiService.post('/merchant/issuing/controls', data);
    return response.data;
  }

  static async listControls(): Promise<{
    id: string;
    name: string;
    type: string;
    parameters: any;
    enabled: boolean;
    created_at: string;
  }[]> {
    const response = await ApiService.get('/merchant/issuing/controls');
    return safeArray(response.data);
  }

  static async getControl(id: string): Promise<{
    id: string;
    name: string;
    type: string;
    parameters: any;
    enabled: boolean;
    created_at: string;
  }> {
    const response = await ApiService.get(`/merchant/issuing/controls/${id}`);
    return response.data;
  }

  static async updateControl(id: string, data: any): Promise<{
    id: string;
    name: string;
    type: string;
    parameters: any;
    enabled: boolean;
    updated_at: string;
  }> {
    const response = await ApiService.put(`/merchant/issuing/controls/${id}`, data);
    return response.data;
  }

  static async deleteControl(id: string): Promise<void> {
    await ApiService.delete(`/merchant/issuing/controls/${id}`);
  }

  // 统计和报表
  static async getCardStats(period: 'day' | 'week' | 'month' | 'year' = 'month'): Promise<{
    total_cards: number;
    active_cards: number;
    blocked_cards: number;
    total_spend: number;
    transaction_count: number;
    average_transaction: number;
    spending_by_category: {
      category: string;
      amount: number;
      count: number;
    }[];
    monthly_trends: {
      month: string;
      cards_issued: number;
      total_spend: number;
      transaction_count: number;
    }[];
  }> {
    const response = await ApiService.get('/merchant/issuing/stats', { period });
    return response.data;
  }

  static async getCardUsageReport(
    cardId: string,
    dateFrom: string,
    dateTo: string
  ): Promise<{
    card: VirtualCard;
    summary: {
      total_transactions: number;
      total_amount: number;
      average_amount: number;
      declined_transactions: number;
    };
    transactions: CardTransaction[];
    categories: {
      category: string;
      amount: number;
      count: number;
    }[];
  }> {
    const response = await ApiService.get(`/merchant/issuing/cards/${cardId}/usage`, {
      date_from: dateFrom,
      date_to: dateTo,
    });
    return response.data;
  }

  // 批量操作
  static async bulkUpdateCards(
    cardIds: string[],
    operation: 'activate' | 'block' | 'unblock' | 'cancel',
    reason?: string
  ): Promise<{
    success_count: number;
    failed_count: number;
    failures: {
      card_id: string;
      error: string;
    }[];
  }> {
    const response = await ApiService.post('/merchant/issuing/cards/bulk', {
      card_ids: cardIds,
      operation,
      reason,
    });
    return response.data;
  }

  // 导出功能
  static async exportCardReport(
    format: 'csv' | 'excel' | 'pdf',
    filters?: CardFilters
  ): Promise<{
    job_id: string;
    status: string;
  }> {
    const response = await ApiService.post('/merchant/issuing/export', {
      type: 'cards',
      format,
      filters,
    });
    return response.data;
  }

  static async exportTransactionReport(
    format: 'csv' | 'excel' | 'pdf',
    filters?: TransactionFilters
  ): Promise<{
    job_id: string;
    status: string;
  }> {
    const response = await ApiService.post('/merchant/issuing/export', {
      type: 'transactions',
      format,
      filters,
    });
    return response.data;
  }
}

export default CardService; 